import fs from 'node:fs';
import path from 'node:path';
import pc from 'picocolors';
import { ApiClient, deriveBaseDomain, type PlatformConfig } from '../client.js';
import { DEFAULT_PRODUCT_NAME } from '../constants.js';
import { gitInitCommit } from '../lib/git.js';
import { buildScaffold, writeScaffold, templatesRoot } from '../lib/scaffold.js';
import { resolveTheme } from '../lib/branding.js';
import { deriveSlug, isValidSlug, slugError } from '../lib/slug.js';
import { DEFAULT_BRAND_COLOR, type TemplateTokens } from '../lib/substitute.js';
import { resolveProfile } from '../profiles.js';
import { emitJson, fail, human, note, ok, warn, type Io } from '../output.js';

export interface InitOptions {
  slug?: string;
  name?: string;
  description?: string;
  backend?: string;
  /** Hidden `--database` escape hatch; undefined/false = no database (default). */
  database?: boolean;
  /** Hidden `--file-storage` escape hatch; undefined/false = none (default). */
  fileStorage?: boolean;
  dir?: string;
  yes?: boolean;
  json?: boolean;
  adminUrl?: string;
}

interface Answers {
  name: string;
  slug: string;
  description: string;
  backend: 'node' | 'python';
  database: boolean;
  fileStorage: boolean;
}

function defaultsFrom(options: InitOptions, targetDir: string): Answers {
  const name = options.name ?? options.slug ?? path.basename(targetDir);
  const slug = options.slug ?? deriveSlug(name);
  return {
    name,
    slug,
    description: options.description ?? '',
    backend: options.backend === 'python' ? 'python' : 'node',
    database: options.database ?? false,
    fileStorage: options.fileStorage ?? false,
  };
}

/** Interview — TTY only, every question skippable via flags / --yes. */
async function interview(options: InitOptions, targetDir: string): Promise<Answers> {
  const defaults = defaultsFrom(options, targetDir);
  const interactive =
    options.yes !== true && options.json !== true && process.stdin.isTTY && process.stdout.isTTY;
  if (!interactive) return defaults;

  // Loaded lazily so non-interactive runs never pay for the prompt library.
  const { input, select } = await import('@inquirer/prompts');

  const name =
    options.name ??
    (await input({
      message: 'What do you want to call your app?',
      default: defaults.name,
      validate: (v) => (v.trim().length > 0 && v.trim().length <= 60) || 'Use 1-60 characters.',
    }));

  const slug =
    options.slug ??
    (await input({
      message: 'URL name (slug):',
      default: deriveSlug(name),
      validate: (v) => slugError(v.trim()) ?? true,
    }));

  const description =
    options.description ??
    (await input({
      message: 'One-line description (optional):',
      default: '',
      validate: (v) => v.length <= 200 || 'Use 200 characters or fewer.',
    }));

  const backend =
    options.backend === 'node' || options.backend === 'python'
      ? (options.backend as 'node' | 'python')
      : await select<'node' | 'python'>({
          message: 'What kind of backend?',
          choices: [
            {
              value: 'node',
              name: 'Web application — dashboards, forms, APIs (Node.js)',
            },
            {
              value: 'python',
              name: 'Data & analytics — data processing, reports (Python)',
            },
          ],
          default: 'node',
        });

  // Apps scaffold with the database DISABLED. We never ask: in practice the
  // builder never requests one up front — it's enabled on demand later by the
  // `database-model` agent (which flips app.config.json `database` and
  // re-provisions). A hidden `--database` flag still forces it on if passed.
  // File storage follows the same model (the `file-storage` agent).
  const database = options.database ?? false;
  const fileStorage = options.fileStorage ?? false;

  return { name: name.trim(), slug: slug.trim(), description: description.trim(), backend, database, fileStorage };
}

export async function runInit(options: InitOptions): Promise<void> {
  const io: Io = { json: options.json === true, productName: DEFAULT_PRODUCT_NAME };

  const resolved = resolveProfile(options.adminUrl);
  if (!resolved) {
    fail(io, 'Not signed in. Run `ghosty login` first — init needs your company settings.', 1, 'NOT_LOGGED_IN');
  }
  io.productName = resolved.profile.branding?.productName ?? DEFAULT_PRODUCT_NAME;

  const targetDir = path.resolve(process.cwd(), options.dir ?? '.');
  const answers = await interview(options, targetDir);

  if (!isValidSlug(answers.slug)) {
    fail(io, slugError(answers.slug)!, 1, 'INVALID_SLUG');
  }
  if (answers.name.length === 0 || answers.name.length > 60) {
    fail(io, 'App name must be 1-60 characters.', 1, 'INVALID_NAME');
  }

  // Public stamp facts for token substitution. Live values beat cache.
  let platform: PlatformConfig | undefined;
  try {
    platform = await new ApiClient(resolved.adminUrl).fetchConfig();
  } catch (err) {
    warn(io, `Could not refresh platform config (${(err as Error).message}); using cached values.`);
  }
  const productNameValue = platform?.productName ?? io.productName;
  io.productName = productNameValue;
  const baseDomain = deriveBaseDomain(resolved.adminUrl, platform);
  const region = platform?.gcpRegion ?? '';
  const stampProject = platform?.gcpProjectId ?? '';
  if (!region || !stampProject) {
    warn(
      io,
      'This admin-app does not report gcpRegion/gcpProjectId yet — the scaffold will have empty {{GHOSTY_REGION}}/{{GHOSTY_STAMP_PROJECT}} values.',
    );
  }

  const tokens: TemplateTokens = {
    GHOSTY_SLUG: answers.slug,
    GHOSTY_APP_NAME: answers.name,
    GHOSTY_DESCRIPTION: answers.description,
    GHOSTY_BASE_DOMAIN: baseDomain,
    GHOSTY_ADMIN_URL: resolved.adminUrl,
    GHOSTY_PRODUCT_NAME: productNameValue,
    GHOSTY_REGION: region,
    GHOSTY_STAMP_PROJECT: stampProject,
    GHOSTY_BRAND_COLOR:
      platform?.colors?.primary ??
      resolved.profile.branding?.colors?.primary ??
      DEFAULT_BRAND_COLOR,
  };

  // Bake the company's FULL brand theme (mode + palette + fonts + logo). Use
  // the stamp's pre-derived theme when /api/config returns one; else derive it
  // from the raw brand inputs (colours.primary + mode/font/logoUrl) via the
  // bundled @ghosty/branding; else fall back to Ghosty's house brand.
  const theme = resolveTheme(platform, resolved.profile.branding);

  if (fs.existsSync(targetDir) && fs.readdirSync(targetDir).length > 0) {
    warn(io, `${targetDir} is not empty — existing files may be overwritten.`);
  }

  let written: string[];
  try {
    const files = buildScaffold({
      backend: answers.backend,
      database: answers.database,
      tokens,
      theme,
      // Bake the complete app.config.json from the same structured inputs the
      // admin web path uses, so both produce an identical file (the scaffolder
      // is the single writer of app.config.json — no separate write here).
      appConfig: {
        slug: answers.slug,
        name: answers.name,
        description: answers.description,
        database: answers.database,
        fileStorage: answers.fileStorage,
        backend: answers.backend,
        adminUrl: resolved.adminUrl,
        company: resolved.profile.company ?? platform?.tenantId ?? '',
      },
      // The CLI ships its OWN bundled templates copy (build artifact), not
      // @ghosty/scaffolder's — resolve relative to the CLI dist.
      templatesDir: templatesRoot(),
    });
    writeScaffold(targetDir, files);
    written = files.map((f) => f.path);
  } catch (err) {
    fail(io, (err as Error).message, 1, 'SCAFFOLD_FAILED');
  }

  // app.config.json is written by the scaffold itself (buildScaffold's
  // appConfig post-pass), so there's no separate write step here.
  const gitNote = gitInitCommit(targetDir, `Initial scaffold for ${answers.slug}`);

  if (io.json) {
    emitJson({
      ok: true,
      dir: targetDir,
      slug: answers.slug,
      name: answers.name,
      backend: answers.backend,
      database: answers.database,
      filesWritten: written.length,
      git: gitNote,
      next: 'ghosty provision',
    });
    return;
  }

  human(io);
  ok(io, `Scaffolded ${pc.bold(answers.name)} (${answers.slug}) in ${targetDir}`);
  ok(io, `Backend: ${answers.backend === 'python' ? 'Python' : 'Node.js'} · Database: ${answers.database ? 'yes' : 'no'} · ${written.length} files`);
  ok(io, 'Wrote app.config.json');
  note(io, `  ${gitNote}`);
  human(io);
  human(io, `${pc.green('ready')} — next: run ${pc.bold('ghosty provision')} to put it on ${productNameValue}.`);
}
